/**
 * The undercarriage: what happens between the wheels and the ground.
 *
 * ## What the sources give, and what they do not
 *
 * The manual is unusually specific about *procedure* and silent about
 * *numbers*. Take-off (03-flight-reference.md, "The Take Off"): WWI machines
 * were **tail-draggers with a skid, not a tailwheel**; full throttle, then
 * "push the stick forward… this will lift the tail off the ground", but "be
 * careful to avoid pushing too far forward on the stick, or you may find your
 * propeller plowing into the ground!"; with the tail up "the aircraft is more
 * streamlined and will gain speed rapidly"; and "when your aircraft gets up at
 * **about 40 mph**, gently pull back on the stick to lift your aircraft into
 * the air". Landing, same chapter: throttle back, "approach the landing field
 * with as little speed as possible (**slightly above stall speed**)", nose up,
 * "**do not try to land with your nose below the horizon**", and "the best
 * landing is a **three-point landing** — that is, when the wheels and tail skid
 * all meet the ground simultaneously". There are **no brakes and no flaps**
 * ("The aircraft of World War One did not have flaps or brakes. Keep this in
 * mind when you land.").
 *
 * The *data* gives nothing at all. `<PLANE>.DAT` carries one structural scalar
 * (wing strength, offset 104) and two radii, and no undercarriage field of any
 * kind — flight-tunables.md §5.2's "what is not there" list. So every constant
 * below is port-side and lives in `CALIBRATION_PENDING_TUNABLES`; what is
 * *not* port-side is the shape, which is the manual's paragraph turned into
 * mechanics one at a time.
 *
 * ## The model
 *
 * Ground attitude is **geometric, not integrated**. A taildragger on its wheels
 * has exactly one degree of pitch freedom — rotate about the main wheels until
 * the skid touches — so while the wheels are down this module *sets* the pitch
 * from the elevator rather than letting the body-rate integrator wander:
 *
 *     tailLift = (forward stick) · (airspeed authority) · gearTailLiftGain
 *     pitch    → groundPitchRad · (1 − tailLift)
 *
 * with a short lag so it reads as the tail coming up rather than snapping. Three
 * of the manual's four take-off sentences fall straight out of that one line:
 * neutral stick holds the three-point attitude, forward stick lifts the tail
 * (and *only* once there is airflow over the elevator — `airspeedAuthority` is
 * the same `(V/Vfull)²` the air uses), and full forward stick at speed drives
 * the pitch past `propStrikePitchRad` and puts the propeller into the ground.
 *
 * The fourth sentence — liftoff at about 40 mph — is not scripted anywhere. It
 * *emerges*: at the three-point attitude the wing is already at a large angle of
 * attack, so lift passes weight a little above the stall speed and the aeroplane
 * flies itself off. `liftoffSpeedMps` reports where that is; on the Camel it
 * lands within a few mph of the manual's figure, and the acceptance test in
 * `flight-ground.test.ts` is written against the manual, not against the
 * constant.
 *
 * `groundPitchRad` is capped at a fraction of each aircraft's *own* stall angle
 * for the same reason: a fleet-wide 16° would sit past the break on the
 * low-`clMax` types, which would mean an aeroplane that cannot leave the ground
 * without stalling off it.
 *
 * ## Where the wheels are is geometry, not a tunable (user report, 2026-08-25)
 *
 * *"The ground camera shows the player floating above the ground when sitting
 * still at the aerodrome."* Every aeroplane in the fleet stood
 * `gearHeightM = 1.15 m` above the surface — one port-side number for
 * thirty-three airframes, from a Morane parasol to a Handley Page — while the
 * *drawn* aeroplane stands wherever its own lowest part reaches. Measured
 * against the baked meshes at each type's own ground attitude the two disagreed
 * by **+0.02 m to +0.60 m on the scouts** (the Camel floated 0.35 m, the Dr.I
 * 0.48 m) and by **−0.39 m to −3.20 m on the big two-seaters and bombers**,
 * which were not floating but buried: a parked Handley Page had 3.2 m of itself
 * underground.
 *
 * So the stance is no longer a number. `aircraft-model-frame.ts`'s
 * `MODEL_GROUND_HULL_UNITS` carries the part of each drawn airframe that can
 * touch the ground, and `stanceHeightM` answers how high the pose origin stands
 * with that shape pitched to a given attitude and resting on a surface. Two
 * things follow that a constant could not give:
 *
 * - **the attitude and the height agree.** A taildragger rises as its tail
 *   comes up, because it is pivoting on its wheels — 0.82 m at the Camel's
 *   three-point attitude and 1.41 m with the fuselage level. A constant put the
 *   tail-up Camel a quarter-metre into the aerodrome, which is the same
 *   fall-through the ground surface itself had a day earlier, arriving from the
 *   other side.
 * - **`groundPitchRad` has a geometric ceiling.** `restingPitchRad` is the
 *   attitude at which the wheels and the skid touch *together* — the manual's
 *   own three-point landing, read off the drawn shape — and it is the furthest
 *   nose-up an aeroplane on its wheels can be: past it, the tail is on the
 *   ground and the wheels are in the air. The fleet's own resting attitudes run
 *   7.0°–18.8°, so the 18° tunable was past the airframe on all but three of
 *   them and a three-point landing was drawn as a tail-sit. The tunable is now
 *   a cap on that, alongside the stall-angle cap, with
 *   `gearStaticPitchFloorDeg` under it for the fourteen meshes whose tails are
 *   too crude to read an attitude off.
 *
 * The numbers above are against the authentic mesh, because that is the one the
 * report was about; the hull the sim actually stands on is that mesh **and** the
 * enhanced `.glb` together, for the reason `MODEL_GROUND_HULL_UNITS` gives.
 */

import { airspeedAuthority } from "./controls.ts";
import type { AeroSpec } from "./aero.ts";
import type { AircraftKey } from "./aircraft-table.ts";
import {
  MODEL_GROUND_HULL_UNITS,
  restingPitchRad,
  stanceHeightM,
  type HullPointUnits,
} from "./aircraft-model-frame.ts";
import {
  SURFACE_GEAR_LIMIT_SCALE,
  SURFACE_ROLLING_RESISTANCE_SCALE,
  type SurfaceKind,
} from "./ground.ts";
import type { FlightInputs } from "./inputs.ts";
import { attitudeFromAngles, headingAngle, pitchAngle, type BodyRates, type Quat, type Vec3 } from "./math3d.ts";
import type { Tunables } from "./tunables.ts";
import { GRAVITY_MPS2, SEA_LEVEL_DENSITY_KGM3, clamp, degreesToRadians } from "./units.ts";

/** The per-aircraft undercarriage, derived in `aircraft-spec.ts` from the aero spec and the tunables. */
export interface UndercarriageSpec {
  /**
   * The part of the drawn airframe that can touch the ground
   * (`aircraft-model-frame.ts`), carried on the spec so `stanceHeightM` is a
   * lookup rather than a table read at every contact tick. Plain data, so a
   * spec still survives a structured clone.
   */
  readonly groundHullUnits: readonly HullPointUnits[];
  /** How far the pose origin sits above the surface at the three-point attitude — `stanceHeightM` at `groundPitchRad`. */
  readonly gearHeightM: number;
  /** Three-point (tail-skid-down) pitch attitude — the manual's own resting stance. */
  readonly groundPitchRad: number;
  /** Pitch below which the propeller reaches the ground. Negative. */
  readonly propStrikePitchRad: number;
  /** Descent rate the gear absorbs on a prepared aerodrome; above it the undercarriage folds. */
  readonly limitDescentRateMps: number;
  /** Bank at touchdown a wheel-and-a-half arrival survives. */
  readonly limitBankRad: number;
  /** Bank at touchdown past which it is a wing strike, not a landing. */
  readonly crashBankRad: number;
  /**
   * Sea-level speed at which lift at the three-point attitude equals weight —
   * where the aeroplane flies itself off, and the reference the HUD and the AI
   * use for "fast enough". Derived, never tuned.
   */
  readonly liftoffSpeedMps: number;
}

/** Wheels-down state, carried in `SimState` so a snapshot resumes a roll mid-way. */
export interface GearState {
  readonly onGround: boolean;
  /** The undercarriage has folded: no liftoff, no steering, and a dead propeller. */
  readonly collapsed: boolean;
  /** A safe arrival that has come to a stop. Latches until the aeroplane rolls again. */
  readonly stopped: boolean;
}

export const GEAR_AIRBORNE: GearState = { onGround: false, collapsed: false, stopped: false };
export const GEAR_PARKED: GearState = { onGround: true, collapsed: false, stopped: true };

export function buildUndercarriage(
  key: AircraftKey,
  aero: AeroSpec,
  massKg: number,
  tunables: Tunables,
): UndercarriageSpec {
  const groundHullUnits = MODEL_GROUND_HULL_UNITS[key];
  // Both stall-referenced quantities here are *wing* angles and `groundPitchRad`
  // is an **attitude**, so the wing's incidence separates them: an aeroplane
  // sitting at attitude θ has its wing at `θ + wingIncidenceRad`. The cap keeps
  // the *wing* under its break, which means the attitude it permits is that much
  // lower.
  const stallCappedPitchRad = aero.alphaStallRad * tunables.gearStaticPitchStallFraction - tunables.wingIncidenceRad;
  const groundPitchRad = Math.min(
    degreesToRadians(tunables.gearStaticPitchDeg),
    stallCappedPitchRad,
    // The airframe's own resting attitude — past it the tail is on the ground
    // and the wheels are in the air, so no elevator setting can sit an
    // aeroplane there. Floored, because six of the meshes carry a tail skid too
    // crude to read an attitude off (`gearStaticPitchFloorDeg`).
    Math.max(restingPitchRad(groundHullUnits), degreesToRadians(tunables.gearStaticPitchFloorDeg)),
  );
  return {
    groundHullUnits,
    gearHeightM: stanceHeightM(groundHullUnits, groundPitchRad),
    groundPitchRad,
    propStrikePitchRad: degreesToRadians(tunables.gearPropStrikePitchDeg),
    limitDescentRateMps: tunables.gearLimitDescentRateMps,
    limitBankRad: degreesToRadians(tunables.gearLimitBankDeg),
    crashBankRad: degreesToRadians(tunables.gearCrashBankDeg),
    // …and the same separation on the way out: `liftoffSpeedMps` asks where the
    // *wing* makes its weight, so it wants the wing's angle at the ground
    // attitude, not the attitude itself.
    liftoffSpeedMps: liftoffSpeedMps(
      aero,
      massKg,
      groundPitchRad + tunables.wingIncidenceRad,
      SEA_LEVEL_DENSITY_KGM3,
    ),
  };
}

/**
 * Where lift at a held angle of attack equals weight. Uses the linear part of
 * the lift curve directly rather than `liftCoefficient`, because
 * `groundPitchRad` is capped below the break by construction.
 */
export function liftoffSpeedMps(
  aero: AeroSpec,
  massKg: number,
  alphaRad: number,
  densityKgM3: number,
): number {
  const cl = Math.max(aero.clAlphaPerRad * alphaRad, 1e-3);
  return Math.sqrt((2 * massKg * GRAVITY_MPS2) / (densityKgM3 * aero.wingAreaM2 * cl));
}

export const TOUCHDOWN_VERDICTS = ["safe", "gearCollapsed", "crash"] as const;

export type TouchdownVerdict = (typeof TOUCHDOWN_VERDICTS)[number];

export interface TouchdownInput {
  readonly gear: UndercarriageSpec;
  readonly surface: SurfaceKind;
  readonly tunables: Tunables;
  /** Positive downward, metres per second. */
  readonly descentRateMps: number;
  readonly bankRad: number;
  readonly pitchRad: number;
}

/**
 * How an arrival went. The three verdicts are the manual's own three endings
 * seen from the aeroplane's side: you taxi in, you write the machine off and
 * walk away, or you crash ("If you crash, you may either walk away with no
 * injuries, end up in a hospital, or be killed, depending upon the severity of
 * the crash" — 04-gameplay.md).
 *
 * Surface scales the *gear* limits and not the crash threshold: a soft field is
 * what folds an undercarriage that a strip would have taken, but the descent
 * rate that kills a pilot does not care what is under him.
 */
export function judgeTouchdown(input: TouchdownInput): TouchdownVerdict {
  const { gear, tunables } = input;
  const bank = Math.abs(input.bankRad);
  if (input.descentRateMps > tunables.crashDescentRateMps) return "crash";
  if (bank > gear.crashBankRad) return "crash";
  const scale = SURFACE_GEAR_LIMIT_SCALE[input.surface];
  if (scale <= 0) return "gearCollapsed";
  if (input.descentRateMps > gear.limitDescentRateMps * scale) return "gearCollapsed";
  if (bank > gear.limitBankRad * scale) return "gearCollapsed";
  // "Do not try to land with your nose below the horizon" — the manual's advice
  // is stricter than this limit, which is where the propeller actually reaches.
  if (input.pitchRad < gear.propStrikePitchRad) return "gearCollapsed";
  return "safe";
}

export interface GroundRollInput {
  readonly gear: UndercarriageSpec;
  readonly collapsed: boolean;
  readonly surface: SurfaceKind;
  readonly tunables: Tunables;
  readonly inputs: FlightInputs;
  readonly velocity: Vec3;
  readonly orientation: Quat;
  /** Lift this tick, so the wheels carry less as the wing starts to fly. */
  readonly liftN: number;
  readonly weightN: number;
  readonly dt: number;
}

export interface GroundRollResult {
  readonly velocity: Vec3;
  readonly orientation: Quat;
  readonly rates: BodyRates;
  /** Speed along the ground track, metres per second. */
  readonly speedMps: number;
  /** The propeller reached the ground during this roll. */
  readonly propStruck: boolean;
}

/** Unit vector along a compass heading, in the world frame (`math3d.ts`: 0 = −Z). */
function headingDirection(headingRad: number): Vec3 {
  return { x: Math.sin(headingRad), y: 0, z: -Math.cos(headingRad) };
}

/** Rolling resistance as a deceleration, faded out by whatever lift the wing is already carrying. */
function rollingDecelMps2(input: GroundRollInput): number {
  const { tunables } = input;
  const onWheels = clamp(1 - input.liftN / Math.max(input.weightN, 1), 0, 1);
  const base = input.collapsed ? tunables.gearCollapsedResistance : tunables.gearRollingResistance;
  return base * SURFACE_ROLLING_RESISTANCE_SCALE[input.surface] * GRAVITY_MPS2 * onWheels;
}

/** The three-point attitude the elevator is currently holding the aeroplane at. */
function groundPitchTargetRad(input: GroundRollInput, speedMps: number): number {
  if (input.collapsed) return 0;
  const tailLift =
    clamp(-input.inputs.pitch, 0, 1) *
    airspeedAuthority(Math.abs(speedMps), input.tunables) *
    input.tunables.gearTailLiftGain;
  return input.gear.groundPitchRad * (1 - tailLift);
}

/**
 * One tick of rolling. There are no brakes in this war, so the only things that
 * slow an aeroplane down are rolling resistance and the drag the force model has
 * already applied; the only thing that steers it is the rudder against the skid,
 * and only once there is enough speed for that to bite.
 */
export function rollOnGround(input: GroundRollInput): GroundRollResult {
  const { tunables, dt } = input;
  const heading = headingAngle(input.orientation);
  const dir = headingDirection(heading);
  const along = input.velocity.x * dir.x + input.velocity.z * dir.z;

  const decel = rollingDecelMps2(input) * dt;
  const speed = Math.abs(along) <= decel ? 0 : along - Math.sign(along) * decel;

  const steer = input.collapsed
    ? 0
    : clamp(input.inputs.yaw, -1, 1) *
      degreesToRadians(tunables.gearSteerRateDegPerSec) *
      clamp(Math.abs(speed) / Math.max(tunables.gearSteerFullSpeedMps, 1e-3), 0, 1);
  const nextHeading = heading + steer * dt;
  const nextDir = headingDirection(nextHeading);

  // The wheels track the nose: whatever sideways velocity the aeroplane arrived
  // with is scrubbed off rather than carried, which is what stops a crabbed
  // touchdown from sliding across the field for ever.
  const grip = clamp(dt * tunables.gearLateralGripPerSecond, 0, 1);
  const lateralX = (input.velocity.x - along * dir.x) * (1 - grip);
  const lateralZ = (input.velocity.z - along * dir.z) * (1 - grip);

  const pitch = pitchAngle(input.orientation);
  const target = groundPitchTargetRad(input, speed);
  const nextPitch = pitch + (target - pitch) * clamp(dt / Math.max(tunables.gearPitchLagSeconds, 1e-3), 0, 1);

  return {
    velocity: { x: nextDir.x * speed + lateralX, y: 0, z: nextDir.z * speed + lateralZ },
    orientation: attitudeFromAngles(nextHeading, nextPitch, 0),
    rates: { roll: 0, pitch: 0, yaw: steer },
    speedMps: speed,
    propStruck: !input.collapsed && nextPitch < input.gear.propStrikePitchRad,
  };
}
